import { apiConfig } from '../config/authConfig';
import type { StatusPayload } from '../types';

/** Carries the HTTP status so the UI can distinguish 403 (access denied) etc. */
export class StatusApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'StatusApiError';
  }
}

let tokenProvider: (() => Promise<string>) | null = null;

/** Wire the MSAL token acquisition in once the user is authenticated. */
export function setTokenProvider(provider: () => Promise<string>): void {
  tokenProvider = provider;
}

/** GET /api/status with a Bearer token. Throws StatusApiError on non-2xx. */
export async function fetchStatus(): Promise<StatusPayload> {
  if (!tokenProvider) {
    throw new StatusApiError(0, 'Not signed in yet.');
  }

  let token: string;
  try {
    token = await tokenProvider();
  } catch (err) {
    throw new StatusApiError(401, `Could not acquire an access token: ${String(err)}`);
  }

  let res: Response;
  try {
    res = await fetch(`${apiConfig.baseUrl}/status`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (err) {
    throw new StatusApiError(0, `Network error: ${String(err)}`);
  }

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (body && typeof body.error === 'string') {
        message = body.error;
      }
    } catch {
      // non-JSON error body; keep the generic message
    }
    throw new StatusApiError(res.status, message);
  }

  return (await res.json()) as StatusPayload;
}
